// 입고예정 통합관리 — «CAPA 모니터링» 탭(운영팀 시안 ①).
// 머리줄(전산 동기화·다음 판정·[새로고침]·[CAPA · 알림 설정]) · 날짜 알약(오늘·익일) · 요약 4칸
// · 센터별 현황 카드(가동률 내림차순 · 게이지 0~120 · 표시선 = 주의 기준·100) · 입고요청 상세(안내 문구 · 요청번호별 ·
//   메신저로 공유 · 요청 목록 엑셀) · 최근 알림 이력 · 향후 입고 부하 표(칸 → 그 센터·날짜) · 각주.
// CAPA 미설정 단위는 카드·부하 표에 넣지 않는다 — 카드 아래 접힌 줄(ic-unset-list)에만 두고, 누르면 설정 탭(onGoSettings).
// «SKU 마스터 미등록» 칸의 «환산 기준 보기»는 SKU 적재 기준 탭의 미등록 목록으로 간다(onShowUnregistered).
// 🔴 가동률·단계·PLT·EA·요약은 서버 값만 그린다 — 판정은 서버 inbound_capa_service 가 끝냈다.
//    화면이 하는 셈은 상세 안내 문구의 차이(예정−CAPA · 주의 구간까지 여유)와 게이지 위치뿐이다.
// 🔴 시각: run.finished_at 은 UTC naive → fmtKstDateTime · next_slot 은 KST naive → 글자 그대로(시간대 변환 금지).
// 🔴 [새로고침] — 진행 중에는 비활성. 응답 cached=false 면 다시 읽어 그린다 · cached=true 면 «N초 전 판정한 결과» 안내
//    · 409 면 서버 문구(다른 판정이 진행 중…)를 같은 자리에 띄운다 — 아무 일도 없는 것처럼 보이지 않게.
// 🔴 미집계 안내(ic-unjudged)는 성공한 판정이 한 번도 없을 때만. 마지막 판정 실패·설정 변경 뒤 미판정은 머리줄 아래 작은 띠(ic-stale).
import { useCallback, useEffect, useRef, useState } from 'react';
import { inboundCapaAPI } from '../../api/client';
import { alertDialog, toast } from '../../components/AppDialog';
import { copyToClipboard } from '../../utils/clipboard';
import { fmtKstDateTime, fmtKstShort } from '../../utils/kstTime';
import { COLORS, TONES, btn, card } from '../../utils/uiStyles';

const DASH = '—';
const WEEKDAYS = '일월화수목금토';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const NAVY = '#16325C';
// 시안 ① 색 — main = 채움·글자 · soft = 칸·안내 배경 · ink = 옅은 배경 위 글자(노랑 main 은 글자로 읽기 어렵다)
const TONE = {
  normal: { main: '#2E75D4', soft: '#EAF1FB', ink: '#2E75D4', label: '정상' },
  warn: { main: '#D99A00', soft: '#FFE4A3', ink: '#8A6100', label: '주의' },
  over: { main: '#D92D20', soft: '#F8C4BE', ink: '#B42318', label: '초과' },
};
const ALERT_KINDS = {
  over: { tone: TONE.over, label: '초과' },
  warn: { tone: TONE.warn, label: '주의' },
  release: { tone: TONE.normal, label: '해제' },
};
const UNIT_LABELS = { plt: 'PLT', vehicle: '대' };
const GAUGE_MAX = 120;              // 게이지 오른쪽 끝 = 120%(초과도 얼마나 넘었는지 보이게)
const ALERTS_LIMIT = 20;
const BUSY_FALLBACK = '다른 판정이 진행 중입니다 — 잠시 뒤 다시 눌러 주세요';
// 맨 아래 각주 — 운영팀 요청 문구 그대로(req-20261026-095719-d5a8b9). 글자를 바꾸지 않는다.
export const FOOTNOTE = '예상 PLT = 예정수량 ÷ (박스입수 × PLT당 박스수) · 취소·입고완료 요청 제외 · 요청번호+SKU 기준 중복 제거 · 같은 센터·날짜는 단계가 바뀔 때만 다시 알림';
const panel ={ ...card, padding: '14px 16px', minWidth: 0 };
const panelHead = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px', marginBottom: '10px' };
const panelTitle = { margin: 0, fontSize: '15px' };
const panelAside = { marginLeft: 'auto', fontSize: '12px', color: COLORS.textMuted };
const th = { padding: '8px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '6px 8px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}` };
const num = { textAlign: 'right', whiteSpace: 'nowrap' };
const smallNote = { fontSize: '12px', color: COLORS.textMuted };
const band = { borderRadius: '6px', padding: '6px 10px', fontSize: '12px', marginBottom: '10px' };
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const fmt = (v) => (isNum(v) ? v.toLocaleString('ko-KR') : DASH);
const unitOf = (u) => UNIT_LABELS[u?.basis] || UNIT_LABELS.plt;
const warnOf = (s) => (isNum(s?.warn_pct) ? s.warn_pct : 90);
const overOf = (s) => (isNum(s?.over_pct) ? s.over_pct : 100);
const pctText = (cell) => (isNum(cell?.pct) ? `${cell.pct}%` : DASH);
const cellOn = (u, d) => (u?.cells || []).find((c) => c.plan_date === d) || null;
const judged = (cell) => !!cell && cell.stage !== 'unset';
const pctKey = (cell) => (isNum(cell?.pct) ? cell.pct : -1);
const markLeft = (v) => (Math.max(0, Math.min(GAUGE_MAX, v)) / GAUGE_MAX * 100).toFixed(1);
const fileSafe = (s) => String(s || '').replace(/[\\/:*?"<>|\s]+/g, '_');
const tag = (soft, ink) => ({
  display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '999px', fontSize: '12px',
  lineHeight: '18px', fontWeight: 600, whiteSpace: 'nowrap', background: soft, color: ink,
});
const grayTag = tag(TONES.gray.soft, TONES.gray.ink);
const stageTag = (stage) => (TONE[stage] ? tag(TONE[stage].soft, TONE[stage].ink) : grayTag);

function dateParts(iso) {
  if (!iso || !ISO_DATE.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return { m, d, wd: WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] };
}

// '2026-10-24' → '10/24(토)'. 요일은 날짜로 계산한다(UTC 자정으로 읽어 브라우저 시간대에 안 흔들린다). 설정·SKU 탭도 쓴다.
export function dayLabel(iso) {
  const p = dateParts(iso);
  return p ? `${p.m}/${p.d}(${p.wd})` : (iso || DASH);
}

// 날짜 알약·상세 제목 — '2026-10-21' → '10/21 (수)'.
export function pillLabel(iso) {
  const p = dateParts(iso);
  return p ? `${p.m}/${p.d} (${p.wd})` : (iso || DASH);
}

const mdLabel = (iso) => {
  const p = dateParts(iso);
  return p ? `${p.m}/${p.d}` : (iso || DASH);
};

// KST naive('2026-10-23T17:30:00') → '2026-10-23 17:30'. 서버가 이미 KST 로 준 값이라 변환하지 않는다.
export function kstNaiveText(value) {
  const s = String(value || '');
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : '';
}

// 상세 안내 문구 — 단계는 서버 값 · 차이만 화면이 뺀다(예정·CAPA 는 서버 값).
export function detailMessage(cell, unit, settings) {
  if (!cell) return '이 날은 이 센터의 영업일이 아닙니다.';
  if (!judged(cell) || !isNum(cell.capa)) return 'CAPA 미설정 — CAPA · 알림 설정에서 일 CAPA를 넣으면 판정합니다.';
  const u = unitOf(unit);
  const planned = isNum(cell.planned) ? cell.planned : 0;
  const capa = cell.capa;
  if (cell.stage === 'over') {
    if (planned > capa) {
      return `CAPA를 ${fmt(planned - capa)} ${u} 초과했습니다. 다른 날짜로 입고 일정을 분산하거나 인원·도크 추가 배치가 필요합니다.`;
    }
    if (planned === capa) return 'CAPA에 도달했습니다. 추가 입고요청은 다른 날짜로 안내해 주세요.';
    return `초과 기준(${overOf(settings)}%) 이상입니다. CAPA까지 ${fmt(capa - planned)} ${u} 남았습니다. 추가 입고요청은 다른 날짜로 안내해 주세요.`;
  }
  if (cell.stage === 'warn') {
    if (planned >= capa) {
      return `주의 구간입니다. CAPA를 ${fmt(planned - capa)} ${u} 넘었습니다. 추가 입고요청 접수 시 일정 조정을 검토해 주세요.`;
    }
    return `주의 구간입니다. CAPA까지 ${fmt(capa - planned)} ${u} 남았습니다. 추가 입고요청 접수 시 일정 조정을 검토해 주세요.`;
  }
  const warn = warnOf(settings);
  const room = Math.max(0, Math.ceil(capa * warn / 100) - planned);
  return `주의 구간(${warn}%)까지 ${fmt(room)} ${u} 여유가 있습니다.`;
}

// 메신저로 공유 — 상세에 보이는 센터·날짜의 서버 값 + 안내 문구.
export function detailShareText(unit, cell, date, settings) {
  if (!unit) return '';
  const lines = [`[입고 CAPA] ${unit.name || ''} · ${pillLabel(date)}`];
  if (judged(cell)) {
    const est = cell.estimated ? ' (추정 포함)' : '';
    lines.push(`예정 ${fmt(cell.planned)} / CAPA ${fmt(cell.capa)} ${unitOf(unit)} (${pctText(cell)}) ${TONE[cell.stage]?.label || cell.stage}${est}`);
  }
  lines.push(detailMessage(cell, unit, settings));
  return lines.join('\n');
}

// 서버 오류 문구 — 설정·SKU 탭도 같은 것을 쓴다(detail 을 그대로 보인다).
export function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return fallback;
}

// jsdom 16 에는 Blob.text() 가 없다 → FileReader 갈래로 읽는다(네이버 CAPA 화면과 같다). SKU 탭도 쓴다.
export const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

function Gauge({ cell, warn }) {
  const tone = TONE[cell.stage] || TONE.normal;
  const pct = isNum(cell.pct) ? cell.pct : 0;
  const fill = Math.max(0, Math.min(GAUGE_MAX, pct)) / GAUGE_MAX * 100;
  const marks = [warn, 100];
  return (
    <span data-testid="ic-gauge" data-pct={isNum(cell.pct) ? cell.pct : ''} style={{ display: 'block', margin: '10px 0 2px' }}>
      <span style={{ display: 'block', position: 'relative', height: '8px', background: '#EEF0F3', borderRadius: '4px' }}>
        <span style={{ display: 'block', width: `${fill}%`, height: '100%', background: tone.main, borderRadius: '4px' }} />
        {marks.map((v, i) => (
          <span key={`m${i}`} data-testid="ic-gauge-mark" data-pct={v} data-left={markLeft(v)} title={`${v}%`}
            style={{ position: 'absolute', top: '-3px', bottom: '-3px', left: `${markLeft(v)}%`, width: '2px', marginLeft: '-1px', background: NAVY, opacity: 0.6 }} />
        ))}
      </span>
      <span aria-hidden="true" style={{ display: 'block', position: 'relative', height: '14px', fontSize: '10px', color: COLORS.textMuted }}>
        {marks.map((v, i) => (
          <span key={`l${i}`} data-testid="ic-gauge-label"
            style={{ position: 'absolute', top: '2px', left: `${markLeft(v)}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
            {v}%
          </span>
        ))}
      </span>
    </span>
  );
}

function UnitCard({ u, cell, warn, selected, onPick }) {
  const tone = TONE[cell.stage] || TONE.normal;
  const temp = u.temp_zone || u.temp_label;
  return (
    <button type="button" data-testid="ic-unit-card" data-unit-id={u.id} data-stage={cell.stage}
      data-selected={selected ? 'true' : 'false'} aria-pressed={selected} onClick={() => onPick(u.id)}
      style={{ display: 'block', width: '100%', minWidth: 0, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: COLORS.text,
        background: COLORS.surface, borderRadius: '8px', padding: '12px 14px',
        border: `1px solid ${selected ? TONE.normal.main : COLORS.border}`, boxShadow: selected ? `0 0 0 1px ${TONE.normal.main}` : 'none' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <b style={{ fontSize: '14px', overflowWrap: 'anywhere' }}>{u.name}</b>
        {temp && <span style={grayTag}>{temp}</span>}
        <span data-testid="ic-unit-stage" style={stageTag(cell.stage)}>{tone.label}</span>
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '8px' }}>
        <span data-testid="ic-unit-planned" style={{ fontSize: '24px', fontWeight: 700 }}>{fmt(cell.planned)}</span>
        <span style={{ fontSize: '12px', color: COLORS.textSub }}>/ {fmt(cell.capa)} {unitOf(u)}</span>
        <span data-testid="ic-unit-pct" style={{ marginLeft: 'auto', fontSize: '18px', fontWeight: 700, color: tone.ink }}>{pctText(cell)}</span>
      </span>
      <Gauge cell={cell} warn={warn} />
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12px', color: COLORS.textSub }}>
        <span data-testid="ic-unit-basis">
          {u.basis === 'vehicle' ? `기준: 차량대수 · 차량당 ${fmt(u.plt_per_vehicle)} PLT` : '기준: PLT 수'}
        </span>
        {cell.estimated && <span data-testid="ic-unit-estimated" style={tag(TONE.warn.soft, TONE.warn.ink)}>추정 포함</span>}
      </span>
    </button>
  );
}

function UnsetList({ items, onGoSettings }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div data-testid="ic-unset-list" data-open={open ? 'true' : 'false'}
      style={{ marginTop: '10px', borderTop: `1px dashed ${COLORS.border}`, paddingTop: '8px' }}>
      <button type="button" data-testid="ic-unset-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        style={{ ...btn('quiet', 'sm'), padding: '2px 0', color: COLORS.textSub }}>
        <span aria-hidden="true">{open ? '▾ ' : '▸ '}</span>CAPA 미설정 {items.length}곳 — 설정하면 판정합니다
      </button>
      {open && (
        <ul style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '13px' }}>
          {items.map(({ u, cell }) => (
            <li key={u.id} style={{ marginBottom: '2px' }}>
              {onGoSettings ? (
                <button type="button" data-testid="ic-unset-item" data-unit-id={u.id} onClick={onGoSettings}
                  title="CAPA · 알림 설정 탭으로 갑니다"
                  style={{ ...btn('quiet', 'sm'), padding: 0, fontSize: '13px', color: COLORS.primaryDark, textDecoration: 'underline' }}>
                  {u.name} · 예정 {fmt(cell.plt_equiv)} PLT
                </button>
              ) : (
                <span data-testid="ic-unset-item" data-unit-id={u.id}>{u.name} · 예정 {fmt(cell.plt_equiv)} PLT</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function putTags(p) {
  const out = [];
  if (p.revised) out.push(['revised', '수정 반영', tag(TONE.normal.soft, TONE.normal.ink)]);
  if (p.estimated) out.push(['estimated', '추정 포함', tag(TONE.warn.soft, TONE.warn.ink)]);
  if (p.excluded) out.push(['excluded', '환산 제외', grayTag]);
  if (!out.length) out.push(['confirmed', '확정', grayTag]);
  return out;
}

function Stat({ label, value, color, testId }) {
  return (
    <div style={{ flex: '1 1 90px', minWidth: 0, background: '#F6F7F9', borderRadius: '6px', padding: '8px 10px' }}>
      <div style={{ fontSize: '12px', color: COLORS.textSub }}>{label}</div>
      <div data-testid={testId} style={{ fontSize: '17px', fontWeight: 700, color: color || COLORS.text, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

function DetailPanel({ unitId, unit, cell, date, settings, detail, exporting, onShare, onExport }) {
  const current = detail?.date === date && detail?.unitId === unitId ? detail : null;    // 다른 센터·날짜의 응답은 그리지 않는다
  const data = current?.data || null;
  const tone = TONE[cell?.stage] || null;
  const u = unitOf(unit);
  const puts = data?.puts || [];
  const totals = data?.totals || {};
  return (
    <section data-testid="ic-detail" data-unit-id={unitId} data-date={date} style={panel}>
      <div style={{ ...panelHead, marginBottom: '6px' }}>
        <h3 style={panelTitle}>입고요청 상세</h3>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
        <b data-testid="ic-detail-title" style={{ fontSize: '15px', overflowWrap: 'anywhere' }}>{unit?.name || `단위 ${unitId}`} · {pillLabel(date)}</b>
        {judged(cell) && <span data-testid="ic-detail-stage" style={stageTag(cell.stage)}>{tone ? tone.label : cell.stage}</span>}
        {cell && !judged(cell) && <span data-testid="ic-detail-stage" style={grayTag}>CAPA 미설정</span>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
        <Stat label="예정" testId="ic-detail-planned" value={cell ? `${fmt(cell.planned)} ${u}` : DASH} />
        <Stat label="CAPA" testId="ic-detail-capa" value={judged(cell) && isNum(cell.capa) ? `${fmt(cell.capa)} ${u}` : DASH} />
        <Stat label="가동률" testId="ic-detail-pct" value={judged(cell) ? pctText(cell) : DASH} color={tone?.ink} />
      </div>
      <div data-testid="ic-detail-message" data-stage={cell?.stage || ''} role="note"
        style={{ borderRadius: '6px', padding: '9px 12px', fontSize: '13px', lineHeight: 1.5, marginBottom: '10px',
          background: tone ? tone.soft : '#F6F7F9', color: tone ? COLORS.text : COLORS.textSub }}>
        {detailMessage(cell, unit, settings)}
      </div>
      {current?.loading && <div role="status" style={smallNote}>불러오는 중…</div>}
      {current?.error && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '12px' }}>{current.error}</div>}
      {data && (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="입고요청 목록" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>고객사 · 요청번호</th>
                <th style={{ ...th, ...num }}>예정 EA</th>
                <th style={{ ...th, ...num }}>예상 PLT</th>
                <th style={th}>상태</th>
              </tr>
            </thead>
            <tbody>
              {puts.map((p) => (
                <tr key={`p-${p.put_sno}`} data-testid="ic-detail-put" data-put-sno={p.put_sno}
                  data-revised={p.revised ? 'true' : 'false'} data-estimated={p.estimated ? 'true' : 'false'}>
                  <td style={{ ...td, overflowWrap: 'anywhere' }}>
                    <div data-testid="ic-put-shipper">{p.pwn_nm || p.pwn_cd || DASH}</div>
                    <div data-testid="ic-put-sno" style={{ fontSize: '11px', color: COLORS.textMuted }}>{p.put_sno}</div>
                  </td>
                  <td style={{ ...td, ...num }}>{fmt(p.ea)}</td>
                  <td style={{ ...td, ...num }}>{fmt(p.plt)}</td>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px' }}>
                      {putTags(p).map(([kind, label, style]) => (
                        <span key={kind} data-testid="ic-put-tag" data-kind={kind} style={style}>{label}</span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
              {puts.length === 0 && (
                <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>이 날 입고요청이 없습니다</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr data-testid="ic-detail-total">
                <td style={{ ...td, fontWeight: 700 }}>합계 {fmt(totals.puts)}건</td>
                <td style={{ ...td, ...num }}><b data-testid="ic-detail-ea" data-value={isNum(totals.ea) ? totals.ea : ''}>{fmt(totals.ea)}</b></td>
                <td style={{ ...td, ...num }}><b>{fmt(totals.plt)}</b></td>
                <td style={td} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <div style={{ ...smallNote, marginTop: '6px' }}>추정 포함 = SKU 마스터 미등록분을 기본 적재 기준으로 계산</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
        <button type="button" data-testid="ic-share" onClick={onShare} disabled={!unit} style={btn('primary', 'sm')}>메신저로 공유</button>
        <button type="button" data-testid="ic-export" onClick={onExport} disabled={exporting}
          style={{ ...btn('secondary', 'sm'), opacity: exporting ? 0.6 : 1 }}>
          {exporting ? '내려받는 중…' : '요청 목록 엑셀'}
        </button>
      </div>
    </section>
  );
}

function AlertsBox({ alerts }) {
  const items = alerts?.items || [];
  return (
    <section data-testid="ic-alerts" style={panel}>
      <div style={panelHead}>
        <h3 style={panelTitle}>최근 알림 이력</h3>
        {alerts?.channel && <span data-testid="ic-alerts-channel" style={panelAside}>{alerts.channel}</span>}
      </div>
      {alerts === null && <div role="status" style={smallNote}>불러오는 중…</div>}
      {alerts && items.length === 0 && <div style={{ fontSize: '13px', color: COLORS.textMuted }}>보낸 알림이 없습니다</div>}
      {items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '320px', overflowY: 'auto' }}>
          {items.map((a) => {
            const kind = ALERT_KINDS[a.kind];
            const hasNumbers = isNum(a.planned) && isNum(a.capa);
            const name = a.center_name || a.unit_name || '시험 발송';
            const when = fmtKstShort(a.created_at) || DASH;
            return (
              <li key={a.id} data-testid="ic-alert-item" data-kind={a.kind || ''}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '7px 0', borderBottom: `1px solid ${COLORS.borderSoft}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {a.plan_date ? `${name} · ${pillLabel(a.plan_date)}` : name}
                  </div>
                  <div style={{ fontSize: '12px', color: COLORS.textSub, overflowWrap: 'anywhere' }}>
                    {hasNumbers
                      ? `${fmt(a.planned)} / ${fmt(a.capa)} ${unitOf(a)} (${isNum(a.pct) ? a.pct : DASH}%) · 발송 ${when}`
                      : `${String(a.message || '').split('\n')[0]} · 발송 ${when}`}
                  </div>
                </div>
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
                  <span data-testid="ic-alert-stage" style={kind ? tag(kind.tone.soft, kind.tone.ink) : grayTag}>{kind ? kind.label : '시험'}</span>
                  {a.ok === false && <span style={tag(TONE.over.soft, TONE.over.ink)}>발송 실패</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function LoadTable({ view, rows, selUnitId, selDate, onOpen }) {
  const dates = view.dates || [];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-testid="ic-load-table" aria-label="향후 입고 부하"
        style={{ width: '100%', borderCollapse: 'collapse', minWidth: `${160 + dates.length * 96}px` }}>
        <thead>
          <tr>
            <th style={th}>센터</th>
            {dates.map((d) => (
              <th key={d} style={{ ...th, textAlign: 'center', color: d === selDate ? NAVY : COLORS.textSub }}>
                {pillLabel(d)}
                {d === view.today && <span style={{ fontWeight: 400 }}> 오늘</span>}
                {d !== view.today && d === view.next_date && <span style={{ fontWeight: 400 }}> 익일</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} data-testid="ic-load-row" data-unit-id={u.id}>
              <th scope="row" style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap' }}>
                <b>{u.name}</b>
                <div style={{ fontSize: '11px', fontWeight: 400, color: COLORS.textMuted }}>{u.basis === 'vehicle' ? '차량 기준' : 'PLT 기준'}</div>
              </th>
              {dates.map((d) => {
                const cell = cellOn(u, d);
                if (!judged(cell)) {
                  return (
                    <td key={d} title={cell ? 'CAPA 미설정' : '영업일 아님'}
                      style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>{DASH}</td>
                  );
                }
                const tone = TONE[cell.stage] || TONE.normal;
                const active = selUnitId === u.id && selDate === d;
                return (
                  <td key={d} style={{ ...td, padding: '3px' }}>
                    <button type="button" data-testid="ic-load-cell" data-unit-id={u.id} data-date={d} data-stage={cell.stage}
                      aria-pressed={active} onClick={() => onOpen(u.id, d)}
                      title={`${u.name} · ${pillLabel(d)} — 예정 ${fmt(cell.planned)} / CAPA ${fmt(cell.capa)} ${unitOf(u)}${cell.estimated ? ' (추정 포함)' : ''}`}
                      style={{ display: 'block', width: '100%', padding: '6px 4px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                        font: 'inherit', textAlign: 'center', background: tone.soft, color: COLORS.text,
                        boxShadow: active ? `inset 0 0 0 2px ${NAVY}` : 'none' }}>
                      <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: tone.ink }}>{pctText(cell)}</span>
                      <span style={{ display: 'block', fontSize: '11px', color: COLORS.textSub }}>{fmt(cell.planned)} / {fmt(cell.capa)}</span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={dates.length + 1} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>판정한 센터가 없습니다</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function MonitorTab({ me, date = '', unit = '', onQuery, onGoSettings, onShowUnregistered }) {
  const [view, setView] = useState(null);
  const [viewError, setViewError] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [note, setNote] = useState(null);           // null | { kind:'cached'|'busy'|'failed'|'error', text }
  const [detail, setDetail] = useState(null);       // null | { unitId, date, data, loading, error }
  const [detailVersion, setDetailVersion] = useState(0);
  const [alerts, setAlerts] = useState(null);       // null | { items, channel }
  const [exporting, setExporting] = useState(false);
  const mountedRef = useRef(true);
  const seqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; seqRef.current += 1; };
  }, []);

  const load = useCallback(async (d) => {
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    setLoading(true);
    try {
      const r = await inboundCapaAPI.view(d || undefined);
      if (!mountedRef.current || seqRef.current !== seq) return;
      setView(r.data || null);
      setViewError('');
    } catch (err) {
      if (!mountedRef.current || seqRef.current !== seq) return;
      setViewError(errorText(err, '입고 CAPA 판정 결과를 불러오지 못했습니다.'));
    } finally {
      if (mountedRef.current && seqRef.current === seq) setLoading(false);
    }
  }, []);

  const loadAlerts = useCallback(() => {
    inboundCapaAPI.alerts(ALERTS_LIMIT)
      .then((r) => {
        if (!mountedRef.current) return;
        setAlerts({ items: Array.isArray(r.data?.items) ? r.data.items : [], channel: r.data?.channel_label || '' });
      })
      .catch(() => { if (mountedRef.current) setAlerts({ items: [], channel: '' }); });
  }, []);

  useEffect(() => { load(date); }, [date, load]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const settings = view?.settings || {};
  const warn = warnOf(settings);
  const over = overOf(settings);
  const units = view?.units || [];
  const dates = view?.dates || [];
  const selDate = date || view?.date || '';
  const onDate = units.map((u) => ({ u, cell: cellOn(u, selDate) })).filter((x) => x.cell);
  // 카드 = 선택 날짜에 판정한 단위만(미설정 제외) · 가동률 내림차순(같으면 서버 순서 그대로)
  const cards = onDate.filter((x) => judged(x.cell)).sort((a, b) => pctKey(b.cell) - pctKey(a.cell));
  const unsetItems = onDate.filter((x) => !judged(x.cell));
  // 부하 표 = 판정한 칸이 하나라도 있는 단위(전부 미설정인 단위는 뺀다)
  const loadRows = units.filter((u) => (u.cells || []).some(judged));
  const urlUnitId = /^\d+$/.test(String(unit || '')) ? Number(unit) : null;
  const selUnitId = urlUnitId !== null ? urlUnitId : (cards[0]?.u.id ?? null);
  const viewUnit = units.find((u) => u.id === selUnitId) || null;
  const detailMatches = !!detail && detail.unitId === selUnitId && detail.date === selDate;
  const selUnit = viewUnit || (detailMatches ? detail.data?.unit : null) || null;
  const selCell = viewUnit ? cellOn(viewUnit, selDate) : (detailMatches ? detail.data?.cell || null : null);

  useEffect(() => {
    if (!selUnitId || !selDate) { setDetail(null); return undefined; }
    let alive = true;
    setDetail({ unitId: selUnitId, date: selDate, data: null, loading: true, error: '' });
    inboundCapaAPI.detail(selUnitId, selDate)
      .then((r) => { if (alive && mountedRef.current) setDetail({ unitId: selUnitId, date: selDate, data: r.data || null, loading: false, error: '' }); })
      .catch((err) => {
        if (alive && mountedRef.current) {
          setDetail({ unitId: selUnitId, date: selDate, data: null, loading: false, error: errorText(err, '상세를 불러오지 못했습니다.') });
        }
      });
    return () => { alive = false; };
  }, [selUnitId, selDate, detailVersion]);

  const pickUnit = (id) => onQuery({ date: selDate, unit: String(id) });
  const openCell = (id, d) => onQuery({ date: d, unit: String(id) });
  const pickDate = (d) => onQuery({ date: d, unit: unit || '' });

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setNote(null);
    try {
      const r = await inboundCapaAPI.refresh();
      const body = r?.data || {};
      if (!mountedRef.current) return;
      if (body.cached) {
        const cooldown = isNum(view?.cooldown_s) ? view.cooldown_s : 60;
        const ago = isNum(body.cooldown_remaining_s) ? Math.max(0, cooldown - body.cooldown_remaining_s) : 0;
        setNote({
          kind: 'cached',
          text: `${ago}초 전 판정한 결과입니다(1분 안 반복 조회는 운영 DB 보호로 다시 계산하지 않습니다 · CAPA·설정을 바꾸면 바로 다시 계산됩니다)`,
        });
        return;
      }
      if (body.run && body.run.ok === false) {
        setNote({ kind: 'failed', text: `판정에 실패해 미집계로 남겼습니다 — ${body.run.error || '서버 로그를 확인해 주세요.'}` });
      }
      await load(date);
      if (mountedRef.current) setDetailVersion((v) => v + 1);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err?.response?.status === 409) setNote({ kind: 'busy', text: errorText(err, BUSY_FALLBACK) });
      else setNote({ kind: 'error', text: errorText(err, '새로고침에 실패했습니다.') });
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  };

  const copyShare = () => {
    copyToClipboard(detailShareText(selUnit, selCell, selDate, settings))
      .then(() => toast('공유 문구를 복사했습니다'))
      .catch(() => alertDialog('복사에 실패했습니다. 화면의 숫자를 직접 옮겨 주세요.'));
  };

  const downloadExcel = async () => {
    if (exporting || !selUnitId || !selDate) return;
    setExporting(true);
    try {
      const r = await inboundCapaAPI.exportXlsx(selDate, selUnitId);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `입고CAPA_입고요청_${selDate}_${fileSafe(selUnit?.name || selUnitId)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let message = errorText(err, '');
      const data = err?.response?.data;
      if (!message && data instanceof Blob) {
        try {
          const parsed = JSON.parse(await readBlobText(data));
          message = typeof parsed?.detail === 'string' ? parsed.detail : '';
        } catch (e) { /* 기본 문구를 쓴다 */ }
      }
      alertDialog(`엑셀 내려받기에 실패했습니다${message ? `: ${message}` : '.'}`);
    } finally {
      if (mountedRef.current) setExporting(false);
    }
  };

  const run = view?.run || null;
  const last = view?.last_attempt || null;
  const lastFailed = !!last && last.ok === false;
  const summary = view?.summary || {};
  const staleReason = run && lastFailed ? 'failed' : run && view?.stale ? 'stale' : '';
  const summaryItems = [
    {
      kind: 'over', label: 'CAPA 초과 센터', value: `${fmt(summary.over || 0)} / ${fmt(summary.judged || 0)}곳`,
      color: summary.over > 0 ? TONE.over.ink : COLORS.text, sub: `${mdLabel(view?.date)} 기준 · 일정 분산 필요`,
    },
    {
      kind: 'warn', label: '주의 구간 센터', value: `${fmt(summary.warn || 0)} / ${fmt(summary.judged || 0)}곳`,
      color: summary.warn > 0 ? TONE.warn.ink : COLORS.text, sub: `CAPA ${warn}% 이상 · 추가 접수 주의`,
    },
    {
      kind: 'plt', label: '전체 예정 PLT', value: `${fmt(summary.planned_plt_total || 0)} PLT`,
      color: COLORS.text, sub: '차량 기준 센터는 차량당 적재 PLT로 환산',
    },
    {
      kind: 'unregistered', label: 'SKU 마스터 미등록', value: `${fmt(summary.unregistered_lines || 0)} 건`,
      color: COLORS.text, sub: null,
    },
  ];

  return (
    <>
      <div data-testid="ic-head"
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '8px 16px', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', color: COLORS.textMuted, marginBottom: '2px' }}>아워박스 운영팀</div>
          <h3 style={{ margin: 0, fontSize: '18px', color: NAVY }}>입고 CAPA 모니터링</h3>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px', fontSize: '12px', color: COLORS.textSub }}>
          <span>
            <span aria-hidden="true" style={{ color: run && !staleReason ? COLORS.success : COLORS.textMuted }}>● </span>
            전산 동기화 <b data-testid="ic-last-run" style={{ color: COLORS.text }}>{(run?.finished_at && fmtKstDateTime(run.finished_at)) || DASH}</b>
          </span>
          <span>다음 판정 <b data-testid="ic-next-run" style={{ color: COLORS.text }}>{kstNaiveText(view?.next_slot) || DASH}</b></span>
          <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
            <button type="button" data-testid="ic-refresh" onClick={refresh} disabled={refreshing}
              style={{ ...btn('primary', 'sm'), opacity: refreshing ? 0.6 : 1 }}>
              {refreshing ? '판정 중…' : '새로고침'}
            </button>
            {onGoSettings && (
              <button type="button" data-testid="ic-go-settings" onClick={onGoSettings} style={btn('secondary', 'sm')}>CAPA · 알림 설정</button>
            )}
          </span>
        </div>
      </div>

      {note && (
        <div data-testid="ic-refresh-note" data-cached={note.kind === 'cached' ? 'true' : 'false'} data-kind={note.kind}
          role={note.kind === 'cached' ? 'status' : 'alert'}
          style={{ ...band, background: note.kind === 'cached' ? COLORS.infoSoft : note.kind === 'busy' ? COLORS.warnSoft : COLORS.dangerSoft,
            color: note.kind === 'cached' ? COLORS.primaryDark : note.kind === 'busy' ? COLORS.warn : COLORS.dangerText }}>
          {note.text}
        </div>
      )}

      {view && staleReason && (
        <div data-testid="ic-stale" data-reason={staleReason} role="status"
          style={{ ...band, background: staleReason === 'failed' ? COLORS.dangerSoft : COLORS.warnSoft,
            color: staleReason === 'failed' ? COLORS.dangerText : COLORS.warn }}>
          {staleReason === 'failed'
            ? `마지막 판정(${fmtKstDateTime(last.finished_at) || DASH})이 실패했습니다: ${last.error || '사유 미상'} · 아래는 ${fmtKstDateTime(run.finished_at) || DASH} 판정 결과입니다.`
            : '설정이 바뀐 뒤 아직 다시 판정하지 않았습니다 — [새로고침]하면 바로 다시 계산합니다.'}
        </div>
      )}

      {me && me.visible === false && (
        <div role="alert" style={{ ...panel, marginBottom: '12px', color: COLORS.textSub, fontSize: '13px' }}>볼 수 있는 센터가 없습니다 — 관리자에게 센터 권한을 요청하세요.</div>
      )}
      {!view && viewError && (
        <div role="alert" style={{ ...panel, marginBottom: '12px', background: COLORS.dangerSoft, color: COLORS.dangerText }}>{viewError}</div>
      )}
      {!view && !viewError && <div role="status" style={{ ...panel, marginBottom: '12px', color: COLORS.textSub, fontSize: '13px' }}>불러오는 중…</div>}

      {view && !run && (
        <div data-testid="ic-unjudged" data-reason="none" role="alert"
          style={{ ...panel, marginBottom: '12px', background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          <span>
            미집계 — 아직 성공한 판정이 없습니다. [새로고침]으로 판정하세요.
            {lastFailed && ` 마지막 시도 실패: ${last.error || '사유 미상'}`}
          </span>
          <button type="button" data-testid="ic-unjudged-refresh" onClick={refresh} disabled={refreshing}
            style={{ ...btn('primary', 'sm'), opacity: refreshing ? 0.6 : 1 }}>
            {refreshing ? '판정 중…' : '새로고침'}
          </button>
        </div>
      )}

      {view && run && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: COLORS.textSub }}>입고예정일</span>
            <div data-testid="ic-date-tabs" role="tablist" aria-label="입고예정일" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {dates.map((d) => {
                const on = d === selDate;
                const mark = d === view.today ? '오늘' : d === view.next_date ? '익일' : '';
                return (
                  <button key={d} type="button" role="tab" aria-selected={on} data-date={d} onClick={() => pickDate(d)}
                    style={{ padding: '5px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: on ? 700 : 500,
                      border: `1px solid ${on ? NAVY : COLORS.border}`, background: on ? NAVY : COLORS.surface, color: on ? '#fff' : COLORS.text }}>
                    {pillLabel(d)}
                    {mark && <span style={{ fontSize: '11px', opacity: 0.85 }}> {mark}</span>}
                  </button>
                );
              })}
              {dates.length === 0 && <span style={smallNote}>판정한 날짜가 없습니다</span>}
            </div>
            {loading && <span role="status" style={smallNote}>불러오는 중…</span>}
            <span data-testid="ic-legend" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: COLORS.textSub }}>
              <span aria-hidden="true" style={{ color: TONE.normal.main }}>●</span>정상 ·{' '}
              <span aria-hidden="true" style={{ color: TONE.warn.main }}>●</span>주의 ≥ {warn}% ·{' '}
              <span aria-hidden="true" style={{ color: TONE.over.main }}>●</span>초과 ≥ {over}%
            </span>
          </div>

          <div data-testid="ic-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '10px', marginBottom: '12px' }}>
            {summaryItems.map((it) => (
              <div key={it.kind} data-testid="ic-summary-item" data-kind={it.kind} style={{ ...panel, padding: '12px 14px' }}>
                <div style={{ fontSize: '12px', color: COLORS.textSub }}>{it.label}</div>
                <div data-testid="ic-summary-value" style={{ fontSize: '22px', fontWeight: 700, color: it.color, margin: '2px 0' }}>{it.value}</div>
                <div style={smallNote}>
                  {it.sub}
                  {it.kind === 'unregistered' && (
                    <>
                      기본 적재 기준으로 추정 ·{' '}
                      {onShowUnregistered ? (
                        <button type="button" data-testid="ic-unregistered-link" onClick={onShowUnregistered}
                          title="SKU 적재 기준 탭의 미등록 SKU 목록으로 갑니다"
                          style={{ ...btn('quiet', 'sm'), padding: 0, fontSize: '12px', textDecoration: 'underline', color: COLORS.primaryDark }}>
                          환산 기준 보기
                        </button>
                      ) : '환산 기준 보기'}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
            <section data-testid="ic-centers" style={{ ...panel, flex: '1 1 640px' }}>
              <div style={panelHead}>
                <h3 style={panelTitle}>센터별 현황 · {pillLabel(selDate)}</h3>
              </div>
              <div data-testid="ic-unit-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: '10px' }}>
                {cards.map(({ u, cell }) => (
                  <UnitCard key={u.id} u={u} cell={cell} warn={warn} selected={u.id === selUnitId} onPick={pickUnit} />
                ))}
              </div>
              {cards.length === 0 && <div style={{ fontSize: '13px', color: COLORS.textMuted }}>이 날 판정한 센터가 없습니다</div>}
              <UnsetList items={unsetItems} onGoSettings={onGoSettings} />
            </section>

            <div style={{ flex: '1 1 360px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selUnitId ? (
                <DetailPanel unitId={selUnitId} unit={selUnit} cell={selCell} date={selDate} settings={settings} detail={detail}
                  exporting={exporting} onShare={copyShare} onExport={downloadExcel} />
              ) : (
                <section data-testid="ic-detail-empty" style={{ ...panel, fontSize: '13px', color: COLORS.textMuted }}>
                  <h3 style={{ ...panelTitle, color: COLORS.text, marginBottom: '6px' }}>입고요청 상세</h3>
                  센터 카드나 부하 표 칸을 누르면 그 센터·날짜의 입고요청을 봅니다.
                </section>
              )}
              <AlertsBox alerts={alerts} />
            </div>
          </div>

          <section data-testid="ic-load" style={{ ...panel, marginBottom: '10px' }}>
            <div style={panelHead}>
              <h3 style={panelTitle}>향후 입고 부하 · {dates.length}영업일</h3>
              <span style={panelAside}>칸 = CAPA 대비 예정 비율 · 누르면 해당 센터·날짜로 이동</span>
            </div>
            <LoadTable view={view} rows={loadRows} selUnitId={selUnitId} selDate={selDate} onOpen={openCell} />
          </section>

          <div data-testid="ic-footnote" style={{ ...smallNote, marginBottom: '8px' }}>{FOOTNOTE}</div>
        </>
      )}
    </>
  );
}
